import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { BuilderState, PlotDraft, emptyDraft } from './plot.models';
import { BridgeService } from './bridge.service';
import { PystService } from './pyst.service';
import {
    Datastream,
    EVENT_WINDOWS,
    Frequency,
    KIND_LABELS,
    KIND_NOTES,
    RAW_WINDOWS,
    StreamKind,
    WINDOW_PRESETS,
    WindowPreset,
    aggregationLabel,
    buildEventFeatureAggregationQuery,
    buildEventFeatureQuery,
    buildForecastQuery,
    buildMultiSeriesQuery,
    buildRawQuery,
    channelGroupOf,
    classifyStream,
    buildEventWindowQuery,
    compatibleStreams,
    displayStreamName,
    EventType,
    FORECAST_VIEWS,
    IGNORED_CHANNELS_CONFIG_NAME,
    normalizeIgnoredChannels,
    usesIgnoredChannel,
    ForecastView,
    frequencyOf,
    incompatibleReason,
    kindSupportsMulti,
    parseResolutionSeconds,
    seriesLabel,
    streamMatchesSearch,
    streamsTitle,
    ThresholdOptions,
    windowsForResolution,
} from './query-templates';

interface DatastreamRow {
    '@iot.id'?: number | string;
    id?: number | string;
    name?: string;
    type?: string;
    bulk?: unknown;
    featureUnit?: string;
    featureName?: string;
    aggregationMethod?: string;
    pipelineName?: string;
    samplingDefinition?: string;
    metadata?: { storageMode?: unknown; virtualCombined?: unknown; sourceDatastreamIdCsv?: unknown; sourceEventType?: unknown; channelName?: unknown } | string | null;
    Channels?: { name?: unknown; unit?: unknown; metadata?: { displayName?: unknown } | null }[] | null;
}

/** A channel/sensor: the first level of the picker. */
interface ChannelGroup {
    name: string;
    streams: Datastream[];
}

/** A frequency within a channel: the second level. */
interface FrequencyGroup extends Frequency {
    streams: Datastream[];
}

/**
 * The channel display name to show next to a stream: only for a stream linked to
 * exactly one channel whose admin-set `metadata.displayName` differs from its raw
 * name (the dashboard's `channelDisplayName` rule). Anything else keeps its name.
 */
function channelDisplayOf(row: DatastreamRow): { raw: string; display: string } | null {
    const channels = Array.isArray(row.Channels) ? row.Channels : [];
    if (channels.length !== 1) {
        return null;
    }
    const raw = String(channels[0]?.name ?? '').trim();
    const display = String(channels[0]?.metadata?.displayName ?? '').trim();
    return raw && display && display !== raw ? { raw, display } : null;
}

/** Source ids of a virtual stream; empty for a stream that stores its own data. */
function sourceIdsOf(row: DatastreamRow): string[] {
    let metadata = row.metadata;
    if (typeof metadata === 'string') {
        try {
            metadata = JSON.parse(metadata);
        } catch {
            return [];
        }
    }
    if (!metadata || typeof metadata !== 'object') {
        return [];
    }
    const virtual = metadata.storageMode === 'source-datastreams' || metadata.virtualCombined === true;
    if (!virtual) {
        return [];
    }
    return String(metadata.sourceDatastreamIdCsv ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => /^\d+$/.test(id));
}

/** Parsed metadata object, or null when absent or not an object. */
function metadataOf(row: DatastreamRow): Record<string, unknown> | null {
    let metadata: unknown = row.metadata;
    if (typeof metadata === 'string') {
        try {
            metadata = JSON.parse(metadata);
        } catch {
            return null;
        }
    }
    return metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : null;
}

/**
 * The unit to put on an axis, from the places it actually lives.
 *
 * The `featureUnit` column is empty for most aggregated channel streams — the
 * unit sits on the channel, or on the datastream's metadata. The dashboard reads
 * all three (its raw view takes `channel.unit`, its indicator builder writes
 * `coalesce(Channels/0/unit, metadata/featureUnit, '')`), so a plot that only
 * read the column ended up labelled "Value".
 */
function unitOf(row: DatastreamRow): string {
    const channels = Array.isArray(row.Channels) ? row.Channels : [];
    const metadata = metadataOf(row);
    const candidates = [
        ...channels.map((channel) => channel?.unit),
        row.featureUnit,
        metadata?.['featureUnit'],
        metadata?.['feature_unit'],
    ];
    for (const candidate of candidates) {
        const text = String(candidate ?? '').trim();
        // "na" is how some pipelines spell "unitless"; it is not a unit.
        if (text && text.toLowerCase() !== 'na') {
            return text;
        }
    }
    return '';
}

function isTrue(value: unknown): boolean {
    return value === true || value === 1 || String(value ?? '').trim().toLowerCase() === 'true';
}

@Component({
    selector: 'app-query-builder-dialog',
    templateUrl: './query-builder-dialog.component.html',
    styleUrls: ['./query-builder-dialog.component.scss'],
})
export class QueryBuilderDialogComponent {
    public streams: Datastream[] = [];
    /** Streams over time, or streams inside events. */
    public mode: 'stream' | 'event' = 'stream';
    public eventTypes: EventType[] = [];
    public eventTypeId: string | null = null;
    public eventCount = 1;
    public loading = true;
    public error: string | null = null;
    public search = '';
    /** The picker's path: channel, then frequency; the leaves are the streams. */
    public channelName: string | null = null;
    public frequencyKey: string | null = null;
    /** Chosen streams, in the order chosen; all drawn in one plot. */
    public selectedIds: string[] = [];
    public preset: WindowPreset | null = null;
    /** Forecast streams: which view, and the configured limit in years. */
    public forecastView: ForecastView = 'card';
    public criticalYears: number | null = null;
    public readonly forecastViews = FORECAST_VIEWS;
    /** Optional warning threshold drawn on value plots. */
    public thresholdValue: number | null = null;
    public thresholdDirection: 'above' | 'below' = 'above';

    public readonly kindLabels = KIND_LABELS;
    public readonly kindNotes = KIND_NOTES;

    /** Dashboard origin for event links, derived from the project's API path. */
    public dashboardOrigin = '';
    public linkEvents = true;

    /** Streams named in a restored selection that no longer exist in the project. */
    public missingStreams: string[] = [];

    /** Whether this dialog opened on an existing plot rather than a blank one. */
    public get reopened(): boolean {
        return !!this.incoming;
    }

    constructor(
        private dialogRef: MatDialogRef<QueryBuilderDialogComponent, PlotDraft>,
        private pyst: PystService,
        private bridges: BridgeService,
        @Optional() @Inject(MAT_DIALOG_DATA) private incoming: BuilderState | null,
    ) {
        this.dashboardOrigin = this.bridges.dashboardOrigin;
        void this.load().then(() => this.restore());
    }

    /**
     * Put the dialog back where a saved plot left it. Streams that have since been
     * deleted are named rather than quietly dropped: the rebuilt query would read
     * fewer streams than the one that was saved, and that should not be a surprise.
     */
    private restore(): void {
        const state = this.incoming;
        if (!state || this.error) {
            return;
        }
        this.mode = state.mode;
        this.selectedIds = state.streamIds.filter((id) =>
            this.streams.some((stream) => stream.id === id),
        );
        this.missingStreams = state.streamIds.filter((id) => !this.selectedIds.includes(id));
        const first = this.selected[0];
        if (first) {
            this.channelName = channelGroupOf(first);
            this.frequencyKey = frequencyOf(first).key;
        }
        this.eventTypeId = state.eventTypeId ?? this.eventTypeId;
        this.eventCount = state.eventCount;
        this.forecastView = (state.forecastView as ForecastView) ?? this.forecastView;
        this.criticalYears = state.criticalYears;
        this.linkEvents = state.linkEvents;
        this.thresholdValue = state.threshold?.value ?? null;
        this.thresholdDirection = state.threshold?.direction ?? 'above';
        // The preset objects are rebuilt per resolution, so match by label.
        const windows = this.windows;
        this.preset = windows.find((window) => window.label === state.presetLabel)
            ?? (windows.length ? windows[Math.min(2, windows.length - 1)] : null);
    }

    /** The selection, in the shape a plot stores so it can be reopened. */
    private builderState(): BuilderState {
        return {
            mode: this.mode,
            streamIds: [...this.selectedIds],
            presetLabel: this.preset?.label ?? null,
            threshold: this.threshold,
            eventTypeId: this.eventTypeId,
            eventCount: this.eventCount,
            forecastView: this.kind === 'forecast' ? this.forecastView : null,
            criticalYears: this.criticalYears,
            linkEvents: this.linkEvents,
        };
    }

    /** Streams the current mode can show at all. */
    private get eligible(): Datastream[] {
        if (this.mode === 'event') {
            return this.streams.filter((stream) => {
                const kind = classifyStream(stream);
                return kind === 'raw' || (kind === 'series' && stream.resolutionSeconds !== null);
            });
        }
        return this.streams;
    }

    /**
     * The picker's lists are read from getters on every change-detection pass, and
     * `windowsForResolution` and the grouping below build fresh objects each time.
     * A new array each pass makes `*ngFor` rebuild its rows under the cursor — so
     * a click never lands — and makes `option === preset` compare two copies of
     * the same window and come out false, so nothing ever looks selected. Each
     * list is therefore computed once per distinct input and reused.
     */
    private memo = new Map<string, { key: string; value: unknown }>();

    /** The value for `name`, rebuilt only when `key` says its inputs changed. */
    private cached<T>(name: string, key: string, build: () => T): T {
        const hit = this.memo.get(name);
        if (hit && hit.key === key) {
            return hit.value as T;
        }
        const value = build();
        this.memo.set(name, { key, value });
        return value;
    }

    /**
     * Channels/sensors, narrowed by the search: a channel stays when its name or any
     * stream in it matches, so `ch1 60m min` still walks down to the right leaf.
     */
    get channels(): ChannelGroup[] {
        return this.cached(
            "channels",
            `${this.mode}:${this.search}:${this.streams.length}`,
            () => this.buildChannels(),
        );
    }

    private buildChannels(): ChannelGroup[] {
        const groups = new Map<string, Datastream[]>();
        for (const stream of this.eligible) {
            const name = channelGroupOf(stream);
            if (!groups.has(name)) {
                groups.set(name, []);
            }
            groups.get(name)!.push(stream);
        }
        const query = this.search.trim();
        const out: ChannelGroup[] = [];
        for (const [name, streams] of groups) {
            if (!query || streams.some((stream) => streamMatchesSearch(stream, query))
                || name.toLowerCase().includes(query.toLowerCase())) {
                out.push({ name, streams });
            }
        }
        return out.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    }

    get channel(): ChannelGroup | null {
        return this.channels.find((group) => group.name === this.channelName) ?? null;
    }

    /** Frequencies present in the chosen channel: raw, each resolution, per event, forecast. */
    get frequencies(): FrequencyGroup[] {
        return this.cached(
            "frequencies",
            `${this.mode}:${this.search}:${this.channelName}:${this.streams.length}`,
            () => this.buildFrequencies(),
        );
    }

    private buildFrequencies(): FrequencyGroup[] {
        const channel = this.channel;
        if (!channel) {
            return [];
        }
        const groups = new Map<string, FrequencyGroup>();
        for (const stream of channel.streams) {
            const frequency = frequencyOf(stream);
            if (!groups.has(frequency.key)) {
                groups.set(frequency.key, { ...frequency, streams: [] });
            }
            groups.get(frequency.key)!.streams.push(stream);
        }
        return [...groups.values()].sort((a, b) => a.order - b.order);
    }

    get frequency(): FrequencyGroup | null {
        return this.frequencies.find((group) => group.key === this.frequencyKey) ?? null;
    }

    /** The streams at the chosen channel and frequency: what is actually picked. */
    get leaves(): Datastream[] {
        return this.cached(
            "leaves",
            `${this.mode}:${this.search}:${this.channelName}:${this.frequencyKey}:${this.streams.length}`,
            () => this.buildLeaves(),
        );
    }

    private buildLeaves(): Datastream[] {
        const frequency = this.frequency;
        if (!frequency) {
            return [];
        }
        return [...frequency.streams].sort((a, b) =>
            aggregationLabel(a).localeCompare(aggregationLabel(b), undefined, { numeric: true }));
    }

    get selected(): Datastream[] {
        return this.cached("selected", `${this.selectedIds.join(",")}:${this.streams.length}`, () =>
            this.selectedIds
                .map((id) => this.streams.find((stream) => stream.id === id))
                .filter((stream): stream is Datastream => !!stream),
        );
    }

    get kind(): StreamKind | null {
        const first = this.selected[0];
        return first ? classifyStream(first) : null;
    }

    /** Multiple streams share one plot only when they share a kind and frequency. */
    get canAddMore(): boolean {
        return !!this.selected.length && kindSupportsMulti(this.kind);
    }

    /**
     * The unit drawn against the right-hand axis, when the selection spans units.
     * The first stream's unit keeps the left axis, matching the query builders.
     */
    get secondAxisUnit(): string | null {
        const units = this.selected
            .map((stream) => stream.unit.trim())
            .filter((unit, index, all) => unit && all.indexOf(unit) === index);
        return units.length > 1 ? units.slice(1).join(', ') : null;
    }

    /** Whether a tick is a checkbox (several possible) or a single choice. */
    multiCapable(stream: Datastream): boolean {
        return kindSupportsMulti(classifyStream(stream));
    }

    pickChannel(group: ChannelGroup): void {
        this.channelName = group.name;
        // Stay on the same frequency when this channel has it — walking across
        // channels at 60 min is the common case — otherwise start at its first.
        const frequencies = this.frequencies;
        if (!frequencies.some((frequency) => frequency.key === this.frequencyKey)) {
            this.frequencyKey = frequencies[0]?.key ?? null;
        }
    }

    pickFrequency(group: FrequencyGroup): void {
        this.frequencyKey = group.key;
    }

    isSelected(stream: Datastream): boolean {
        return this.selectedIds.includes(stream.id);
    }

    /** Whether picking this stream would add to the selection rather than replace it. */
    addsTo(stream: Datastream): boolean {
        const first = this.selected[0];
        return !first || this.isSelected(stream) || compatibleStreams(first, stream);
    }

    /**
     * Ticking a stream that cannot share a plot with the current selection used to
     * discard the selection silently, which is how a careful multi-stream pick was
     * lost to one stray click. Now it explains itself and waits: `replaceWith` is
     * the only way to start over.
     */
    public blocked: { stream: Datastream; reason: string } | null = null;

    toggle(stream: Datastream): void {
        if (this.isSelected(stream)) {
            this.selectedIds = this.selectedIds.filter((id) => id !== stream.id);
            this.blocked = null;
            return;
        }
        const first = this.selected[0];
        if (!first) {
            this.replaceWith(stream);
            return;
        }
        const reason = incompatibleReason(first, stream);
        if (reason) {
            this.blocked = { stream, reason };
            return;
        }
        this.blocked = null;
        this.selectedIds = [...this.selectedIds, stream.id];
        if (!this.preset) {
            this.resetPreset();
        }
    }

    /** Discard the selection and start again from this stream. */
    replaceWith(stream: Datastream): void {
        this.selectedIds = [stream.id];
        this.blocked = null;
        this.resetPreset();
    }

    private resetPreset(): void {
        const windows = this.windows;
        this.preset = windows.length ? windows[Math.min(2, windows.length - 1)] : null;
    }

    remove(stream: Datastream): void {
        this.selectedIds = this.selectedIds.filter((id) => id !== stream.id);
        this.blocked = null;
    }

    label(stream: Datastream): string {
        return seriesLabel(stream);
    }

    aggregationOf(stream: Datastream): string {
        return aggregationLabel(stream);
    }

    kindOf(stream: Datastream): StreamKind {
        return classifyStream(stream);
    }

    /** Only the windows that mean something for the selection's kind. */
    get windows(): WindowPreset[] {
        return this.cached(
            "windows",
            `${this.kind}:${this.selected[0]?.resolutionSeconds ?? ""}`,
            () => this.buildWindows(),
        );
    }

    private buildWindows(): WindowPreset[] {
        switch (this.kind) {
            case 'raw':
                return RAW_WINDOWS;
            case 'series': {
                // A fixed-resolution stream is read at that resolution; the window
                // choices follow from it.
                const resolution = this.selected[0]?.resolutionSeconds ?? null;
                return resolution !== null ? windowsForResolution(resolution) : WINDOW_PRESETS;
            }
            case 'event-feature':
            case 'event-feature-aggregation':
                return EVENT_WINDOWS;
            default:
                return [];
        }
    }

    get eventType(): EventType | null {
        return this.eventTypes.find((type) => type.id === this.eventTypeId) ?? null;
    }

    /** Event links only apply where points are events. */
    get supportsEventLinks(): boolean {
        return this.kind === 'event-feature';
    }

    get supportsThreshold(): boolean {
        const kind = this.kind;
        return kind === 'raw' || kind === 'series' || kind === 'event-feature' || kind === 'event-feature-aggregation';
    }

    private get threshold(): ThresholdOptions | null {
        return this.supportsThreshold && this.thresholdValue !== null && Number.isFinite(this.thresholdValue)
            ? { value: this.thresholdValue, direction: this.thresholdDirection }
            : null;
    }

    get supported(): boolean {
        return !!this.preview;
    }

    get preview(): string {
        const streams = this.selected;
        if (!streams.length) {
            return '';
        }
        const kind = this.kind;
        if (this.mode === 'event') {
            const type = this.eventType;
            if (!type || (kind !== 'raw' && kind !== 'series') || streams.some((stream) => classifyStream(stream) !== kind)) {
                return '';
            }
            if (kind === 'series' && streams.some((stream) => stream.resolutionSeconds === null)) {
                return '';
            }
            return buildEventWindowQuery(streams, { eventType: type, count: this.eventCount });
        }
        const stream = streams[0];
        switch (kind) {
            case 'forecast':
                return buildForecastQuery(stream, { view: this.forecastView, criticalYears: this.criticalYears });
            case 'raw':
                return this.preset ? buildRawQuery(streams, this.preset, this.threshold) : '';
            case 'series':
                return this.preset ? buildMultiSeriesQuery(streams, this.preset, this.threshold) : '';
            case 'event-feature':
                return this.preset
                    ? buildEventFeatureQuery(
                          stream,
                          this.preset,
                          { dashboardOrigin: this.linkEvents ? this.dashboardOrigin : '' },
                          this.threshold,
                      )
                    : '';
            case 'event-feature-aggregation':
                return this.preset ? buildEventFeatureAggregationQuery(stream, this.preset, this.threshold) : '';
            default:
                return '';
        }
    }

    async load(): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            // $apply form so Channels can be expanded with a nested select, the way
            // the dashboard's forecast page reads them.
            const response = await this.pyst.get<{ value?: DatastreamRow[] }>(
                '/Datastreams?$apply=expand(Channels,select(name,unit,metadata))'
                    + '/select(id,name,type,bulk,featureUnit,featureName,aggregationMethod,pipelineName,samplingDefinition,metadata,Channels)'
                    + '/orderby(name asc)/top(1000)',
            );
            const rows = Array.isArray(response?.value) ? response.value : [];
            const ignored = await this.loadIgnoredChannels();
            const types = await this.pyst.get<{ value?: { '@iot.id'?: number | string; id?: number | string; type?: string; displayName?: string }[] }>(
                '/EventTypes?$select=id,type,displayName&$orderby=type asc&$top=500',
            );
            this.eventTypes = (Array.isArray(types?.value) ? types.value : [])
                .map((row) => ({
                    id: String(row['@iot.id'] ?? row.id ?? '').trim(),
                    name: String(row.displayName || row.type || '').trim() || `Event type ${row['@iot.id'] ?? row.id}`,
                }))
                .filter((type) => type.id);
            // A select with nothing chosen is a dead control; start on the first type.
            if (!this.eventTypes.some((type) => type.id === this.eventTypeId)) {
                this.eventTypeId = this.eventTypes[0]?.id ?? null;
            }
            this.streams = rows
                .map((row) => {
                    const id = String(row['@iot.id'] ?? row.id ?? '').trim();
                    const channels = Array.isArray(row.Channels) ? row.Channels : [];
                    const channelNames = channels.map((channel) => String(channel?.name ?? '').trim()).filter(Boolean);
                    const channelLabels = channels
                        .map((channel) => String(channel?.metadata?.displayName ?? '').trim() || String(channel?.name ?? '').trim())
                        .filter(Boolean);
                    return {
                        id,
                        name: String(row.name ?? '').trim() || `Datastream ${id}`,
                        type: String(row.type ?? '').trim(),
                        unit: unitOf(row),
                        aggregation: String(row.aggregationMethod ?? '').trim(),
                        pipelineName: String(row.pipelineName ?? '').trim(),
                        featureName: String(row.featureName ?? '').trim(),
                        sourceIds: sourceIdsOf(row),
                        channelDisplay: channelDisplayOf(row),
                        resolutionSeconds: parseResolutionSeconds(String(row.samplingDefinition ?? '')),
                        channelName: channelNames[0] ?? '',
                        channelNames,
                        channelLabels,
                        raw: isTrue(row.bulk),
                        forecastEventType: String(metadataOf(row)?.sourceEventType ?? '').trim(),
                        forecastChannel: String(metadataOf(row)?.channelName ?? '').trim(),
                    };
                })
                // Channels the admin ignores on the dashboard are hidden here too:
                // a stream built on one is bad data whatever it is called. Streams
                // linked to no channel at all are internal measurements, not sensors.
                .filter((stream) => stream.id && stream.channelNames.length > 0 && !usesIgnoredChannel(stream, ignored));
        } catch (error) {
            console.error('Could not load datastreams', error);
            this.error = 'Could not load datastreams from this project.';
        } finally {
            this.loading = false;
        }
    }

    /** The dashboard's ignore list; an unreadable or missing row hides nothing. */
    private async loadIgnoredChannels(): Promise<Set<string>> {
        try {
            const response = await this.pyst.get<{ value?: { data?: { ignoreChannels?: unknown } | null }[] }>(
                `/Configs?$apply=filter(name eq '${IGNORED_CHANNELS_CONFIG_NAME}')/select(id,data)/top(1)`,
            );
            const data = Array.isArray(response?.value) ? response.value[0]?.data : null;
            return normalizeIgnoredChannels(data && typeof data === 'object' ? data.ignoreChannels : null);
        } catch (error) {
            console.warn('Could not load the ignored channel list', error);
            return new Set<string>();
        }
    }

    use(): void {
        const streams = this.selected;
        const query = this.preview;
        if (!streams.length || !query) {
            return;
        }
        const kind = this.kind;
        const title = streamsTitle(streams);
        if (this.mode === 'event') {
            const type = this.eventType?.name ?? 'event';
            const which = this.eventCount > 1 ? `last ${this.eventCount} events` : 'last event';
            this.dialogRef.close({
                ...emptyDraft(),
                name: `${type} — ${which} — ${title}`,
                query,
                builder: this.builderState(),
                notes: kind === 'raw'
                    ? 'Raw samples inside each event window'
                    : `Columnar at ${streams[0].resolutionSeconds}s inside each event window`,
                cols: 6,
                height: 320,
            });
            return;
        }
        const suffix = kind === 'forecast'
            ? (this.forecastViews.find((entry) => entry.view === this.forecastView)?.label ?? 'forecast').toLowerCase()
            : (this.preset?.label ?? '').toLowerCase();

        this.dialogRef.close({
            ...emptyDraft(),
            name: suffix ? `${title} — ${suffix}` : title,
            query,
            builder: this.builderState(),
            notes: kind === 'forecast'
                ? `Forecast view via ${this.preview.split('(')[0].replace('/~', '~')}`
                : `${this.preset?.label} at ${this.preset?.resolutionLabel}`,
            cols: 6,
            height: kind === 'forecast' ? 260 : 320,
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }
}
